import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
    apiCollectionState,
    collectionsModeleState,
    currentPageModeleAtom,
    loadingState,
    searchQueryModeleAtom,
    sortConfigModeleAtom,
    toastState,
    userDataState
} from '../../atoms/CollectionState';
import genStyles from '../generale.module.css';
import menuStyles from '../resultat/searchActionsMenu.module.css';
import ResultForm from '../resultat/resultatForm';
import TooltipForm from '../tooltipForm';
import CollectionService from '../../service/CollectionService';
import DrawService from '../../service/DrawService';
import { DropdownSelect } from '../dropdownSelect';

const PRESET_COLORS = [
    { name: '#36c980', iconClass: '#36c980' },
    { name: '#a0d44f', iconClass: '#a0d44f' },
    { name: '#c9b957', iconClass: '#c9b957' },
    { name: '#face47', iconClass: '#face47' },
    { name: '#7945ca', iconClass: '#7945ca' },
    { name: '#a15bd5', iconClass: '#a15bd5' },
    { name: '#6499f4', iconClass: '#6499f4' },
    { name: '#76cbf9', iconClass: '#76cbf9' },
    { name: '#b63453', iconClass: '#b63453' },
    { name: '#db465e', iconClass: '#db465e' },
    { name: '#f61e87', iconClass: '#f61e87' },
    { name: '#f75cbf', iconClass: '#f75cbf' },
    { name: '#f7683c', iconClass: '#f7683c' },
    { name: '#f7af54', iconClass: '#f7af54' },
    { name: '#7d5247', iconClass: '#7d5247' },
    { name: '#818181', iconClass: '#818181' },
    { name: '#252525', iconClass: '#252525' },
    { name: '#FFFFFF', iconClass: '#FFFFFF' },
];

const TYPE_OPTIONS = [
    { value: 'marker', label: 'Marqueur', icon: 'fa-solid fa-location-dot' },
    { value: 'textMarker', label: 'Annotation', icon: 'fa-solid fa-text' },
    { value: 'polyline', label: 'Ligne', icon: 'fa-kit fa-draw-polyline-icon' },
    { value: 'circle', label: 'Cercle', icon: 'fa-solid fa-circle' },
    { value: 'rectangle', label: 'Rectangle', icon: 'fa-solid fa-vector-square' },
    { value: 'polygon', label: 'Polygone', icon: 'fa-solid fa-vector-polygon' },
];

const TYPE_INFO = TYPE_OPTIONS.reduce((acc, t) => {
    acc[t.value] = t;
    return acc;
}, {});

const DEFAULT_FORM = {
    libelle: '',
    type: 'rectangle',
    description: '',
    length: 10,
    width: 5,
    points: null, // sommets (décalages en mètres) d'un polygone / d'une ligne dessiné(e) sur la carte
    color: '#92bbf4',
    opacity: 0.5,
    weight: 4,
    fillColor: '#92bbf4',
    fillOpacity: 0.2,
    markerColor: '#252525',
    iconColor: '#ffffff',
    iconClass: 'fa fa-circle',
    markerShape: 'circle',
    textColor: '#252525',
    textSize: 12,
};

// Reconstruit les champs du formulaire à partir d'un modèle existant (édition)
function formFromModele(modele) {
    let options = {};
    let dimension = {};
    try { options = modele.options ? (typeof modele.options === 'string' ? JSON.parse(modele.options) : modele.options) : {}; } catch { options = {}; }
    try { dimension = modele.dimension ? (typeof modele.dimension === 'string' ? JSON.parse(modele.dimension) : modele.dimension) : {}; } catch { dimension = {}; }

    return {
        ...DEFAULT_FORM,
        libelle: modele.libelle || '',
        type: modele.type || 'rectangle',
        description: modele.description || '',
        length: dimension.length ?? DEFAULT_FORM.length,
        width: dimension.width ?? DEFAULT_FORM.width,
        points: dimension.points ?? null,
        color: options.color ?? DEFAULT_FORM.color,
        opacity: options.opacity ?? DEFAULT_FORM.opacity,
        weight: options.weight ?? DEFAULT_FORM.weight,
        fillColor: options.fillColor ?? DEFAULT_FORM.fillColor,
        fillOpacity: options.fillOpacity ?? DEFAULT_FORM.fillOpacity,
        markerColor: options.icon?.options?.markerColor ?? DEFAULT_FORM.markerColor,
        iconColor: options.icon?.options?.iconColor ?? DEFAULT_FORM.iconColor,
        iconClass: options.icon?.options ? `${options.icon.options.prefix} ${options.icon.options.icon}` : DEFAULT_FORM.iconClass,
        markerShape: options.icon?.options?.shape ?? DEFAULT_FORM.markerShape,
        textColor: options.css?.color ?? DEFAULT_FORM.textColor,
        textSize: parseInt(options.css?.['font-size'], 10) || DEFAULT_FORM.textSize,
    };
}

// Construit les payloads dimension/options à envoyer à l'API à partir du formulaire
function buildPayload(form) {
    const needsDimension = ['rectangle', 'polygon', 'circle', 'polyline'].includes(form.type);
    const dimension = needsDimension ? { length: parseFloat(form.length) || 0, width: parseFloat(form.width) || 0 } : null;
    if (dimension && ['polygon', 'polyline'].includes(form.type) && form.points?.length) {
        dimension.points = form.points;
    }

    let options = {};
    if (form.type === 'marker') {
        const [prefix, icon] = (form.iconClass || 'fa fa-circle').split(' ');
        options = {
            icon: DrawService.createExtraMarker({
                icon,
                markerColor: form.markerColor,
                iconColor: form.iconColor,
                shape: form.markerShape,
                prefix,
                svg: true,
            }),
        };
    } else if (form.type === 'textMarker') {
        options = {
            css: { color: form.textColor, 'font-size': `${form.textSize}px` },
        };
    } else if (form.type === 'polyline') {
        options = {
            color: form.color,
            opacity: parseFloat(form.opacity) || 0,
            weight: parseFloat(form.weight) || 0,
        };
    } else {
        // polygon, rectangle, circle
        options = {
            color: form.color,
            opacity: parseFloat(form.opacity) || 0,
            weight: parseFloat(form.weight) || 0,
            fillColor: form.fillColor,
            fillOpacity: parseFloat(form.fillOpacity) || 0,
        };
    }

    return {
        libelle: form.libelle,
        type: form.type,
        description: form.description,
        dimension: dimension ? JSON.stringify(dimension) : null,
        options: JSON.stringify(options),
    };
}

export default function ModeleManagerForm({ onClose, onViewChange }) {
    const api_collection = useRecoilValue(apiCollectionState);
    const user_data = useRecoilValue(userDataState);
    const [modeles, setModeles] = useRecoilState(collectionsModeleState);
    const setLoading = useSetRecoilState(loadingState);
    const setToast = useSetRecoilState(toastState);
    const [sortConfig, setSortConfig] = useRecoilState(sortConfigModeleAtom);
    const [searchQuery, setSearchQuery] = useRecoilState(searchQueryModeleAtom);
    const [currentPage, setCurrentPage] = useRecoilState(currentPageModeleAtom);

    const [view, setView] = useState('list'); // 'list' | 'form'
    const [form, setForm] = useState(DEFAULT_FORM);
    const [editingId, setEditingId] = useState(null);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [modeleToDelete, setModeleToDelete] = useState(null);
    const [isDrawingOnMap, setIsDrawingOnMap] = useState(false);

    // Sécurité : si le composant se démonte pendant un dessin, on retire la forme temporaire de la carte
    useEffect(() => {
        return () => {
            DrawService.stopModeleShapeDraw();
        };
    }, []);

    // Répercute en direct les changements de style sur la forme en cours de dessin
    useEffect(() => {
        if (isDrawingOnMap) DrawService.setModeleShapeStyle(buildPayload(form).options);
    }, [isDrawingOnMap, form.color, form.opacity, form.weight, form.fillColor, form.fillOpacity]);

    // Prévient le parent quand on entre/sort du formulaire, pour masquer le menu d'onglets
    useEffect(() => {
        onViewChange?.(view);
        return () => onViewChange?.('list');
    }, [view]);

    const api = api_collection + user_data.account.port + '/';

    const reload = () => {
        setLoading(true);
        CollectionService.fetchentiteModel(api).then((response) => {
            setModeles(response);
            setLoading(false);
        });
    };

    const openCreate = () => {
        setForm(DEFAULT_FORM);
        setEditingId(null);
        setView('form');
    };

    const openEdit = (row) => {
        setForm(formFromModele(row));
        setEditingId(row.id);
        setView('form');
    };

    const updateField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const startDrawing = () => {
        DrawService.startModeleShapeDraw(form.type, { length: parseFloat(form.length), width: parseFloat(form.width), points: form.points }, buildPayload(form).options);
        setIsDrawingOnMap(true);
    };

    // Reporte dans le formulaire les dimensions (et sommets) de la forme dessinée sur la carte
    const withDrawnDimension = (prev) => {
        const dimension = DrawService.readModeleShapeDimension();
        if (!dimension) return prev;
        return {
            ...prev,
            length: dimension.length,
            width: dimension.width ?? prev.width,
            points: dimension.points ?? null,
        };
    };

    const validateDrawing = () => {
        setForm(withDrawnDimension);
        DrawService.stopModeleShapeDraw();
        setIsDrawingOnMap(false);
    };

    const cancelDrawing = () => {
        DrawService.stopModeleShapeDraw();
        setIsDrawingOnMap(false);
    };

    const backToList = () => {
        cancelDrawing();
        setView('list');
    };

    const onValidate = () => {
        if (!form.libelle || form.libelle.trim() === '') {
            setToast({ message: 'Veuillez donner un nom au modèle.', type: 'warning' });
            return;
        }

        // Dessin encore en cours (pas cliqué sur "Valider les dimensions") : on le prend en compte
        const formToSave = isDrawingOnMap ? withDrawnDimension(form) : form;
        cancelDrawing();

        const payload = buildPayload(formToSave);
        setLoading(true);

        if (editingId) {
            CollectionService.updateEntiteModele(api, editingId, payload).then((response) => {
                if (response.rows) {
                    setToast({ message: response.message, type: 'success' });
                    reload();
                    setView('list');
                } else {
                    setToast({ message: response.message || 'Erreur lors de la mise à jour', type: 'error' });
                }
                setLoading(false);
            });
        } else {
            CollectionService.addEntiteModele(api, payload).then((response) => {
                if (response.id) {
                    setToast({ message: response.message, type: 'success' });
                    reload();
                    setView('list');
                } else {
                    setToast({ message: response.message || 'Erreur lors de la création', type: 'error' });
                }
                setLoading(false);
            });
        }
    };

    const confirmDelete = (row) => {
        setModeleToDelete(row);
        setShowConfirmDelete(true);
    };

    const doDelete = () => {
        setLoading(true);
        CollectionService.deleteEntiteModele(api, modeleToDelete.id).then((response) => {
            if (response.rows) {
                setToast({ message: response.message, type: 'success' });
                setModeles((prev) => prev.filter((m) => m.id !== modeleToDelete.id));
            } else {
                setToast({ message: response.message || 'Erreur lors de la suppression', type: 'error' });
            }
            setLoading(false);
            setShowConfirmDelete(false);
            setModeleToDelete(null);
        });
    };

    const columns = [
        { key: 'libelle', label: 'Nom', selectable: true },
        {
            key: 'type',
            label: 'Type',
            align: 'center',
            width: '60px',
            selectable: true,
            render: (_, row) => (
                <div className="center-align">
                    <i className={TYPE_INFO[row.type]?.icon || 'fa-solid fa-shapes'}></i>
                </div>
            )
        },
        { key: 'description', label: 'Description', selectable: true },
        {
            key: 'actions',
            label: '',
            sortable: false,
            width: '70px',
            render: (_, row) => (
                <>
                    <a onClick={() => openEdit(row)}>
                        <TooltipForm content="Éditer le modèle">
                            <i className={`fa-solid fa-pen-to-square blue-text text-darken-3 ${genStyles.fontSize16} ${genStyles.icomargin}`}></i>
                        </TooltipForm>
                    </a>
                    <a onClick={() => confirmDelete(row)}>
                        <TooltipForm content="Supprimer le modèle">
                            <i className={`fa-solid fa-trash-can red-text text-darken-2 ${genStyles.fontSize16}`}></i>
                        </TooltipForm>
                    </a>
                </>
            )
        },
    ];

    const needsDimension = ['rectangle', 'polygon', 'circle', 'polyline'].includes(form.type);
    const isClosedShape = ['rectangle', 'polygon', 'circle'].includes(form.type);

    return (
        <>
            {view === 'list' && (
                <>
                    <div className="col s12 center-align">
                        <ResultForm
                            data={modeles}
                            columns={columns}
                            onSelectRow={openEdit}
                            pageSize={10}
                            tableHeight="400px"
                            textpaginate="modèles"
                            textnone="Aucun modèle"
                            texteSearch="Rechercher"
                            emptyState={
                                <div className={genStyles.emptyStateCtn}>
                                    <div className={genStyles.emptyStateIcon}>
                                        <i className="fa-solid fa-shapes"></i>
                                    </div>
                                    <p className={genStyles.emptyStateText}>Vous n'avez pas encore de modèle.</p>
                                    <button
                                        type="button"
                                        className={`${genStyles.modalBtn} ${genStyles.modalBtnPrimary}`}
                                        onClick={openCreate}
                                    >
                                        <i className={`fa-sharp fa-regular fa-square-plus ${genStyles.icomargin}`}></i>
                                        Créer un modèle
                                    </button>
                                </div>
                            }
                            sortConfig={sortConfig}
                            setSortConfig={setSortConfig}
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            actionsButtonRight={
                                <TooltipForm content="Ajouter un modèle">
                                    <button
                                        type="button"
                                        className={`${menuStyles.butMenu} ${menuStyles.butMenuPrimary}`}
                                        onClick={openCreate}
                                    >
                                        <i className="fa-sharp fa-regular fa-square-plus"></i>
                                    </button>
                                </TooltipForm>
                            }
                        />
                    </div>
                </>
            )}

            {view === 'form' && (
                <div className="col s12">
                    <div className={`${genStyles.collectionHeaderBar} ${genStyles.mb40}`}>
                        <span className={genStyles.collectionHeaderTitle}>
                            <i className={`fa-solid fa-shapes ${genStyles.bgcolorfirsttext}`}></i>
                            {editingId ? 'Éditer le modèle' : 'Nouveau modèle'}
                        </span>
                        <button className={genStyles.btnBack} onClick={backToList}>
                            <i className="fa fa-arrow-left"></i>
                            Retour
                        </button>
                    </div>

                    <div className={genStyles.editFormSection}>
                        <div className="mb10">
                            <span className={`${genStyles.fontSize15} black-text ${genStyles.noMargin}`}>Nom du modèle</span>
                            <input
                                className={genStyles.inputtCol}
                                type="text"
                                value={form.libelle}
                                maxLength={50}
                                onChange={(e) => updateField('libelle', e.target.value)}
                            />
                        </div>
                        <div className="mb10">
                            <span className={`${genStyles.fontSize15} black-text ${genStyles.noMargin}`}>Type</span>
                            <select
                                className={genStyles.selectCol}
                                value={form.type}
                                onChange={(e) => { cancelDrawing(); updateField('type', e.target.value); updateField('points', null); }}
                            >
                                {TYPE_OPTIONS.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="mb0">
                            <span className={`${genStyles.fontSize15} black-text ${genStyles.noMargin}`}>Description</span>
                            <input
                                className={genStyles.inputtCol}
                                type="text"
                                value={form.description}
                                onChange={(e) => updateField('description', e.target.value)}
                            />
                        </div>
                    </div>

                    {needsDimension && (
                        <div className={genStyles.editFormSection}>
                            <div className={genStyles.titleedsection}>
                                <i className={`fa-solid fa-ruler-combined ${genStyles.fontSize18} ${genStyles.icomargin}`}></i>
                                <span className={`${genStyles.fontSize16} ${genStyles.bold} ${genStyles.bgcolorfirsttext}`}>
                                    Dimensions
                                </span>
                            </div>
                            <div className="row mb0">
                                <div className="col s6">
                                    <span className={`${genStyles.fontSize15} black-text ${genStyles.noMargin}`}>
                                        {form.type === 'circle' ? 'Diamètre (m)' : form.type === 'polyline' ? 'Longueur (m)' : 'Longueur (m)'}
                                    </span>
                                    <input
                                        className={genStyles.inputtCol}
                                        type="number"
                                        min="0"
                                        value={form.length}
                                        onChange={(e) => updateField('length', e.target.value)}
                                    />
                                </div>
                                {(form.type === 'rectangle' || form.type === 'polygon') && (
                                    <div className="col s6">
                                        <span className={`${genStyles.fontSize15} black-text ${genStyles.noMargin}`}>Largeur (m)</span>
                                        <input
                                            className={genStyles.inputtCol}
                                            type="number"
                                            min="0"
                                            value={form.width}
                                            onChange={(e) => updateField('width', e.target.value)}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="row mb0 mt10">
                                <div className="col s12">
                                    {!isDrawingOnMap ? (
                                        <button
                                            type="button"
                                            className={`${genStyles.modalBtn} ${genStyles.modalBtnSecondary}`}
                                            onClick={startDrawing}
                                        >
                                            <i className={`fa-solid fa-draw-polygon ${genStyles.icomargin}`}></i>
                                            Dessiner sur la carte
                                        </button>
                                    ) : (
                                        <>
                                            <p className={`${genStyles.fontSize13} black-text mb10`}>
                                                {['polygon', 'polyline'].includes(form.type)
                                                    ? "Une forme est affichée au centre de la carte : déplacez ses sommets ou tirez les points intermédiaires pour en ajouter. La forme et ses dimensions seront enregistrées (pas l'emplacement)."
                                                    : "Une forme est affichée au centre de la carte : redimensionnez-la avec les poignées, seules les dimensions seront enregistrées (pas l'emplacement)."}
                                            </p>
                                            <button
                                                type="button"
                                                className={`${genStyles.modalBtn} ${genStyles.modalBtnPrimary}`}
                                                onClick={validateDrawing}
                                            >
                                                <i className={`fa-solid fa-check ${genStyles.icomargin}`}></i>
                                                Valider les dimensions
                                            </button>
                                            <button
                                                type="button"
                                                className={`${genStyles.modalBtn} ${genStyles.modalBtnSecondary}`}
                                                style={{ marginLeft: '10px' }}
                                                onClick={cancelDrawing}
                                            >
                                                Annuler le dessin
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={genStyles.editFormSection}>
                        <div className={genStyles.titleedsection}>
                            <i className={`fa-solid fa-palette ${genStyles.fontSize18} ${genStyles.icomargin}`}></i>
                            <span className={`${genStyles.fontSize16} ${genStyles.bold} ${genStyles.bgcolorfirsttext}`}>
                                Styles
                            </span>
                        </div>

                        {form.type === 'marker' && (
                            <div className="row mb0">
                                <div className="col s6">
                                    <span className={`${genStyles.fontSize15} black-text ${genStyles.noMargin}`}>Couleur du marqueur</span>
                                    <div className={genStyles.pickctn}>
                                        <DropdownSelect options={PRESET_COLORS} selected={form.markerColor} onChange={(c) => updateField('markerColor', c)} type="color" showCustomColor={true} />
                                        <input className={genStyles.inputtCol} type="text" value={form.markerColor} onChange={(e) => updateField('markerColor', e.target.value)} />
                                    </div>
                                </div>
                                <div className="col s6">
                                    <span className={`${genStyles.fontSize15} black-text ${genStyles.noMargin}`}>Couleur de l'icône</span>
                                    <div className={genStyles.pickctn}>
                                        <DropdownSelect options={PRESET_COLORS} selected={form.iconColor} onChange={(c) => updateField('iconColor', c)} type="color" showCustomColor={true} />
                                        <input className={genStyles.inputtCol} type="text" value={form.iconColor} onChange={(e) => updateField('iconColor', e.target.value)} />
                                    </div>
                                </div>
                                <div className="col s6 mt10">
                                    <span className={`${genStyles.fontSize15} black-text ${genStyles.noMargin}`}>
                                        Icône <a className={`underline_hover blue-text ${genStyles.fontSize13}`} href="https://fontawesome.com/search" target="_blank" rel="noreferrer">Fontawesome</a>
                                    </span>
                                    <input className={genStyles.inputtCol} type="text" placeholder="fa fa-circle" value={form.iconClass} onChange={(e) => updateField('iconClass', e.target.value)} />
                                </div>
                                <div className="col s6 mt10">
                                    <span className={`${genStyles.fontSize15} black-text ${genStyles.noMargin}`}>Forme</span>
                                    <select className={genStyles.selectCol} value={form.markerShape} onChange={(e) => updateField('markerShape', e.target.value)}>
                                        <option value="circle">circle</option>
                                        <option value="square">square</option>
                                        <option value="star">star</option>
                                        <option value="penta">penta</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {form.type === 'textMarker' && (
                            <div className="row mb0">
                                <div className="col s6">
                                    <span className={`${genStyles.fontSize15} black-text ${genStyles.noMargin}`}>Couleur du texte</span>
                                    <div className={genStyles.pickctn}>
                                        <DropdownSelect options={PRESET_COLORS} selected={form.textColor} onChange={(c) => updateField('textColor', c)} type="color" showCustomColor={true} />
                                        <input className={genStyles.inputtCol} type="text" value={form.textColor} onChange={(e) => updateField('textColor', e.target.value)} />
                                    </div>
                                </div>
                                <div className="col s6">
                                    <span className={`${genStyles.fontSize15} black-text ${genStyles.noMargin}`}>Taille du texte</span>
                                    <input className={genStyles.inputtCol} type="number" min="1" value={form.textSize} onChange={(e) => updateField('textSize', e.target.value)} />
                                </div>
                            </div>
                        )}

                        {(form.type === 'polyline' || isClosedShape) && (
                            <div className="row mb0">
                                <div className="col s6">
                                    <span className={`${genStyles.fontSize15} black-text ${genStyles.noMargin}`}>Couleur du contour</span>
                                    <div className={genStyles.pickctn}>
                                        <DropdownSelect options={PRESET_COLORS} selected={form.color} onChange={(c) => updateField('color', c)} type="color" showCustomColor={true} />
                                        <input className={genStyles.inputtCol} type="text" value={form.color} onChange={(e) => updateField('color', e.target.value)} />
                                    </div>
                                </div>
                                <div className="col s3">
                                    <span className={`${genStyles.fontSize15} black-text ${genStyles.noMargin}`}>Opacité</span>
                                    <input className={genStyles.inputtCol} type="number" min="0" max="1" step="0.1" value={form.opacity} onChange={(e) => updateField('opacity', e.target.value)} />
                                </div>
                                <div className="col s3">
                                    <span className={`${genStyles.fontSize15} black-text ${genStyles.noMargin}`}>Épaisseur</span>
                                    <input className={genStyles.inputtCol} type="number" min="0" value={form.weight} onChange={(e) => updateField('weight', e.target.value)} />
                                </div>
                            </div>
                        )}

                        {isClosedShape && (
                            <div className="row mb0 mt10">
                                <div className="col s6">
                                    <span className={`${genStyles.fontSize15} black-text ${genStyles.noMargin}`}>Couleur de remplissage</span>
                                    <div className={genStyles.pickctn}>
                                        <DropdownSelect options={PRESET_COLORS} selected={form.fillColor} onChange={(c) => updateField('fillColor', c)} type="color" showCustomColor={true} />
                                        <input className={genStyles.inputtCol} type="text" value={form.fillColor} onChange={(e) => updateField('fillColor', e.target.value)} />
                                    </div>
                                </div>
                                <div className="col s6">
                                    <span className={`${genStyles.fontSize15} black-text ${genStyles.noMargin}`}>Opacité du remplissage</span>
                                    <input className={genStyles.inputtCol} type="number" min="0" max="1" step="0.1" value={form.fillOpacity} onChange={(e) => updateField('fillOpacity', e.target.value)} />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={genStyles.modalActions}>
                        <button className={`${genStyles.modalBtn} ${genStyles.modalBtnSecondary}`} onClick={backToList}>
                            Annuler
                        </button>
                        <button className={`${genStyles.modalBtn} ${genStyles.modalBtnPrimary}`} onClick={onValidate}>
                            Enregistrer
                        </button>
                    </div>
                </div>
            )}

            {/* Portal dans <body> : sinon l'overlay reste prisonnier du container (transform/overflow parent) */}
            {showConfirmDelete && ReactDOM.createPortal(
                <div className={genStyles.modeleConfirmOverlay}>
                    <div className={genStyles.modeleConfirmBox}>
                        <div className={`${genStyles.modalConfirmIcon} ${genStyles.modalConfirmIconDanger}`}>
                            <i className="fa-solid fa-triangle-exclamation"></i>
                        </div>
                        <p className={genStyles.modalConfirmText}>
                            Êtes-vous sûr de vouloir <b className="red-text">supprimer</b> le modèle{' '}
                            <b>{modeleToDelete?.libelle}</b> ?
                        </p>
                        <div className={genStyles.modalActions}>
                            <button className={`${genStyles.modalBtn} ${genStyles.modalBtnSecondary}`} onClick={() => setShowConfirmDelete(false)}>
                                Annuler
                            </button>
                            <button className={`${genStyles.modalBtn} ${genStyles.modalBtnDanger}`} onClick={doDelete}>
                                Valider
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}
